// src/utils/cache/cards-cache.js
// Cards batch loading and caching
// Single source of truth for cards caching and batch loading

import { LOGGING } from '/settings.js';
import { getCardsSettings } from '/app_core/src/cards/cards-settings.js';
import { createBatchLoader, createSortedBatchKey } from './cache-helpers.js';
import { fetchWithCache } from './data-cache.js';

/**
 * Cards Cache - In-memory cache for spot cards
 * Maps spotId -> cards array
 */
const cardsCache = new Map();

/**
 * Explore Cards Cache - In-memory cache for explore cards
 * Maps cacheKey (areaId-packId-tagIds) -> cards array
 */
const exploreCardsCache = new Map();

/**
 * Map Cards Cache - In-memory cache for map cards
 * Maps mapId (or mapSlug) -> cards array
 */
const mapCardsCache = new Map();

/**
 * Area Cards Cache - In-memory cache for area cards
 * Maps areaId -> cards array
 */
const areaCardsCache = new Map();

/**
 * In-flight batch requests - tracks ongoing batch loads to prevent duplicate calls
 * Maps batchKey (sorted spot IDs joined by comma) -> Promise
 */
const inFlightBatchRequests = new Map();

// Track in-flight area-wide preloads to avoid duplicate API calls
const exploreAreaPreloadsInFlight = new Map();
// Track packs that have been fully loaded (either via batch or full pack fetch)
// Stores keys as "areaId-packId"
const explorePacksFullyLoaded = new Set();
// Master Cache: areaId-packId -> Map(tagId -> cards[])
const explorePackMasterCache = new Map();

/**
 * Area Batch Orchestration - tracks the full process of:
 * 1. Fetching packs for an area
 * 2. Running the batch card fetch for those packs
 * Maps areaId -> Promise
 */
const areaBatchOrchestrationPromises = new Map();

// Create batch loader using shared helper
let cardsSettings = null;
const batchLoadCardsForSpotsImpl = createBatchLoader({
    cache: cardsCache,
    inFlightRequests: inFlightBatchRequests,
    getCacheKey: (spotId) => Number(spotId),
    getBatchKey: (batch) => createSortedBatchKey(batch),
    filterUncached: (spotIds, cache, getCacheKey, ...args) => {
        // Filter out spots that are cached AND have a non-null value
        // null means "error occurred, should retry"
        return spotIds.filter(id => {
            const key = getCacheKey(id, ...args);
            const cached = cache.get(key);
            return cached === undefined || cached === null;
        });
    },
    fetchBatch: async (batch) => {
        // Check if cards are enabled (cache settings to avoid repeated calls)
        if (!cardsSettings) {
            cardsSettings = await getCardsSettings();
        }
        if (!cardsSettings.ENABLED) {
            return {};
        }

        const spotIdsParam = batch.join(',');
        const url = `/app_core/api/sqlite/get-cards-for-spots.php?spot_ids=${spotIdsParam}`;
        const data = await fetchWithCache(url);
        return data;
    },
    processResponse: (data, batch, cache, getCacheKey, allResults, getDefaultValue) => {
        // Track which spots were actually in the API response
        const spotsInResponse = new Set();

        // Expected format: { "spotId": [cards], ... }
        if (data && typeof data === 'object') {
            Object.entries(data).forEach(([spotIdStr, cards]) => {
                const spotId = Number(spotIdStr);
                spotsInResponse.add(spotId);
                const cardArray = Array.isArray(cards) ? cards : [];

                // Filter valid cards based on partner settings (ignore if PARTNERS not set)
                const partnersObj = (cardsSettings && typeof cardsSettings.PARTNERS === 'object') ? cardsSettings.PARTNERS : {};
                const validCards = cardArray.filter(card =>
                    card && card.id && (!card.partner || partnersObj[card.partner] !== false)
                );

                // Store in cache
                const key = getCacheKey(spotId);
                cache.set(key, validCards);
                allResults.set(key, validCards);
            });
        }

        // SAFE: Only cache empty arrays for spots that were EXPLICITLY in the API response
        // This prevents caching empty arrays on network failures or incomplete responses
        batch.forEach(spotId => {
            const key = getCacheKey(spotId);
            // Only cache empty array if spot was in response (confirmed no cards)
            // Don't cache if spot wasn't in response (might be network failure)
            if (!cache.has(key) && spotsInResponse.has(spotId)) {
                cache.set(key, []);
                allResults.set(key, []);
            }
        });
    },
    getCached: (spotIds, cache, getCacheKey, ...args) => {
        const results = new Map();
        spotIds.forEach(id => {
            const key = getCacheKey(id, ...args);
            if (cache.has(key)) {
                results.set(key, cache.get(key));
            }
        });
        return results;
    },
    getDefaultValue: () => null, // null = not fetched yet, [] = confirmed no cards (prevents caching on errors)
    cacheName: 'Cards Cache',
    maxBatchSize: 50
});

/**
 * Batch load cards for multiple spots in a single API call
 * @param {Array<number>} spotIds - Array of spot IDs
 * @returns {Promise<Map<number, Array>>} Map of spotId -> cards array
 */
export async function batchLoadCardsForSpots(spotIds) {
    // Reset settings cache to ensure fresh check
    cardsSettings = null;
    return await batchLoadCardsForSpotsImpl(spotIds);
}

/**
 * Get cached cards for spot IDs
 * @param {Array<number>} spotIds - Array of spot IDs
 * @returns {Map<number, Array>} Map of spotId -> cards array
 */
export function getCachedCards(spotIds) {
    const results = new Map();
    spotIds.forEach(id => {
        const spotId = Number(id);
        const cached = cardsCache.get(spotId);
        // Only return non-null cached values (null means error, should retry)
        if (cached !== undefined && cached !== null) {
            results.set(spotId, cached);
        }
    });
    return results;
}

/**
 * Get cards for a single spot (from cache or fetch if not cached)
 * @param {number} spotId - Spot ID
 * @returns {Promise<Array>} Array of cards for the spot
 */
export async function getCardsForSpot(spotId) {
    const spotIdNum = Number(spotId);

    // Check cache first (but treat null as "not cached" to allow retries on errors)
    const cached = cardsCache.get(spotIdNum);
    if (cached !== undefined && cached !== null) {
        if (LOGGING.ENABLED && LOGGING.CACHE) {
            console.log(`[Cards Cache] Cache HIT for spot ${spotId}`);
        }
        return cached; // Returns [] for confirmed no cards, or [cards] for spots with cards
    }

    // Check if this spot is part of an in-flight batch request
    // Look through in-flight requests to see if any batch contains this spot
    for (const [batchKey, promise] of inFlightBatchRequests.entries()) {
        const batchIds = batchKey.split(',').map(id => Number(id));
        if (batchIds.includes(spotIdNum)) {
            if (LOGGING.ENABLED && LOGGING.CACHE) {
                console.log(`[Cards Cache] Spot ${spotId} is in in-flight batch, waiting...`);
            }
            // Wait for the batch to complete
            await promise;
            // Check cache again after batch completes (treat null as not cached)
            const cachedAfterWait = cardsCache.get(spotIdNum);
            if (cachedAfterWait !== undefined && cachedAfterWait !== null) {
                return cachedAfterWait;
            }
        }
    }

    // Not in cache and not in-flight, batch load (even if just one spot, uses same endpoint)
    const results = await batchLoadCardsForSpots([spotIdNum]);
    const result = results.get(spotIdNum);
    // Return [] if null (error case) or undefined, otherwise return the actual result
    return result !== null && result !== undefined ? result : [];
}

/**
 * Preload cards for spots (batch load in background)
 * This is called after spots are displayed to preload cards
 * @param {Array<number>} spotIds - Array of spot IDs
 * @returns {Promise<void>} Resolves when cards are loaded (or failed)
 */
export async function preloadCardsForSpots(spotIds) {
    if (!spotIds || !Array.isArray(spotIds) || spotIds.length === 0) {
        return;
    }

    // Don't await - load in background
    batchLoadCardsForSpots(spotIds).catch(error => {
        console.warn('[Cards Cache] Error preloading cards:', error);
    });
}

/**
 * Clear cards cache for specific spots
 * @param {Array<number>} spotIds - Array of spot IDs to clear (optional, clears all if not provided)
 */
export function clearCardsCache(spotIds = null) {
    if (spotIds && Array.isArray(spotIds)) {
        spotIds.forEach(id => cardsCache.delete(Number(id)));
        if (LOGGING.ENABLED && LOGGING.CACHE) {
            console.log(`[Cards Cache] Cleared cache for ${spotIds.length} spots`);
        }
    } else {
        cardsCache.clear();
        if (LOGGING.ENABLED && LOGGING.CACHE) {
            console.log('[Cards Cache] Cleared all cards cache');
        }
    }
}

/**
 * Clear all cards cache (spot cards, explore cards, map cards, and area cards)
 * NOTE: Does NOT clear in-flight requests - let them complete to populate cache
 * This prevents duplicate API calls when cache is cleared while batches are loading
 */
export function clearAllCardsCache() {
    cardsCache.clear();
    exploreCardsCache.clear();
    mapCardsCache.clear();
    areaCardsCache.clear();
    explorePackMasterCache.clear();
    explorePacksFullyLoaded.clear();
    // DON'T clear in-flight requests - let them complete to populate cache
    // This way, if cache is cleared while a batch is loading, we don't lose the batch
    if (LOGGING.ENABLED && LOGGING.CACHE) {
        console.log('[Cards Cache] Cleared all cards cache (spots + explore + map + area). In-flight requests will complete.');
    }
}

/**
 * Check if cards are cached for a spot
 * @param {number} spotId - Spot ID
 * @returns {boolean} True if cards are cached
 */
export function hasCachedCards(spotId) {
    const cached = cardsCache.get(Number(spotId));
    // Only return true if cached and not null (null means error, should retry)
    return cached !== undefined && cached !== null;
}

// ===== EXPLORE CARDS CACHING =====

/**
 * Generate cache key for explore cards
 * @param {number} areaId - Area ID
 * @param {number} packId - Pack ID
 * @param {Array<number>} tagIds - Array of tag IDs
 * @returns {string} Cache key
 */
function getExploreCacheKey(areaId, packId, tagIds = []) {
    const sortedTagIds = Array.isArray(tagIds) ? [...tagIds].map(Number).sort((a, b) => a - b).join(',') : '';
    return `${Number(areaId)}-${Number(packId)}-${sortedTagIds}`;
}

/**
 * Orchestrates the full area-wide card batch load.
 * Ensures packs are fetched first, then the batch card fetch is triggered.
 * @param {number} areaId 
 * @returns {Promise<void>}
 */
export async function triggerAreaBatch(areaId) {
    const aId = Number(areaId);
    if (!aId) return;

    if (areaBatchOrchestrationPromises.has(aId)) {
        return areaBatchOrchestrationPromises.get(aId);
    }

    const promise = (async () => {
        try {
            // 1. Get packs for the area (needed to know what to batch)
            const { getPacksForArea } = await import('../spot-queries.js');
            const packs = await getPacksForArea(aId);

            if (!packs || packs.length === 0) {
                if (LOGGING.ENABLED && LOGGING.CACHE) {
                    console.log(`[Cards Cache] No packs found for area ${aId}, skipping batch.`);
                }
                return;
            }

            const packIds = packs.map(p => p.id).filter(Boolean);

            // 2. Trigger the batch card preload
            // This function handles its own in-flight tracking (exploreAreaPreloadsInFlight)
            await preloadAllCardsForArea(aId, packIds);

            if (LOGGING.ENABLED && LOGGING.CACHE) {
                console.log(`[Cards Cache] Orchestrated area batch completed for Area: ${aId}`);
            }
        } catch (error) {
            if (LOGGING.ENABLED && LOGGING.CACHE) {
                console.error(`[Cards Cache] Orchestrated area batch failed for Area: ${aId}`, error);
            }
            // Clear promise on error to allow retry
            areaBatchOrchestrationPromises.delete(aId);
            throw error;
        }
    })();

    areaBatchOrchestrationPromises.set(aId, promise);
    return promise;
}

/**
 * Get cards for explore selection (from cache or fetch if not cached)
 * @param {number} areaId - Area ID
 * @param {number} packId - Pack ID
 * @param {Array<number>} tagIds - Array of tag IDs (optional)
 * @returns {Promise<Array>} Array of cards for the explore selection
 */
export async function getCardsForExplore(areaId, packId, tagIds = []) {
    // Normalize inputs immediately
    const aId = Number(areaId);
    const pId = Number(packId);
    const tIds = Array.isArray(tagIds) ? tagIds.map(Number) : [];

    // NEW: Always ensure area-wide batch is triggered and awaited first.
    // This blocks ANY individual tag request until the "Big Batch" has arrived.
    await triggerAreaBatch(aId);

    const cacheKey = getExploreCacheKey(aId, pId, tIds);

    // Check cache first
    if (exploreCardsCache.has(cacheKey)) {
        if (LOGGING.ENABLED && LOGGING.CACHE) {
            console.log(`[Cards Cache] Explore cache HIT for: ${cacheKey}`);
        }
        return exploreCardsCache.get(cacheKey);
    }

    // Check if cards are enabled
    const settings = await getCardsSettings();
    if (!settings.ENABLED) {
        return [];
    }

    if (LOGGING.ENABLED && LOGGING.CACHE) {
        console.log(`[Cards Cache] Cache MISS for: ${cacheKey}. Attempting local resolve...`);
    }

    // Not in cache, attempt to build from preloaded per-tag caches before calling API
    try {
        if (tIds.length > 0) {
            const singleTagKeys = tIds.map(id => getExploreCacheKey(aId, pId, [id]));
            const allSingleCached = singleTagKeys.every(k => exploreCardsCache.has(k));
            if (allSingleCached) {
                const merged = [];
                const seen = new Set();
                for (const id of tIds) {
                    const arr = exploreCardsCache.get(getExploreCacheKey(aId, pId, [id])) || [];
                    for (const card of arr) {
                        if (!card || !card.id) continue;
                        if (!seen.has(card.id)) {
                            seen.add(card.id);
                            merged.push(card);
                        }
                    }
                }
                // Cache merged result for this multi-tag selection to avoid future API calls
                exploreCardsCache.set(cacheKey, merged);
                if (LOGGING.ENABLED && LOGGING.CACHE) {
                    console.log(`[Cards Cache] Built explore cards from single-tag cache for: ${cacheKey}`);
                }
                return merged;
            }
        }
        // SMART RESOLVER: If the pack is fully loaded in the master cache, filter locally
        const masterKey = `${aId}-${pId}`;
        const masterPackData = explorePackMasterCache.get(masterKey);
        if (masterPackData) {
            const merged = [];
            const seen = new Set();

            // If tagIds is empty, we return all cards for the pack
            // Normalize to Numbers for reliable .includes() check
            const targetTagIds = (tIds.length > 0) ? tIds : null;

            masterPackData.forEach((cards, tid) => {
                const tidNum = Number(tid);
                // If filtering by specific tags, only include them. Otherwise (tagIds is null) include everything.
                if (!targetTagIds || targetTagIds.includes(tidNum)) {
                    cards.forEach(card => {
                        if (card && card.id && !seen.has(card.id)) {
                            seen.add(card.id);
                            merged.push(card);
                        }
                    });
                }
            });

            if (LOGGING.ENABLED && LOGGING.CACHE) {
                console.log(`[Cards Cache] Smart Filter matched ${merged.length} cards locally from Master Cache for ${cacheKey}`);
            }

            // Cache the result for this specific selection to make next lookup even faster
            exploreCardsCache.set(cacheKey, merged);
            return merged;
        }

        // Check if this pack is already considered "fully loaded" for this area
        // If so, and it's not in cache, it means it HAS no cards - don't fetch.
        if (explorePacksFullyLoaded.has(`${aId}-${pId}`)) {
            if (LOGGING.ENABLED && LOGGING.CACHE) {
                console.log(`[Cards Cache] Pack ${pId} in area ${aId} fully loaded; returning empty (no cards).`);
            }
            return [];
        }

        // Not possible to build locally, fall back to API
        const tagIdsParam = (tIds.length > 0) ? tIds.join(',') : '';

        const url = `/app_core/api/sqlite/get-cards-for-explore.php?areaId=${aId}&packId=${pId}${tagIdsParam ? `&tagIds=${tagIdsParam}` : ''}`;

        if (LOGGING.ENABLED && LOGGING.CACHE) {
            console.log(`[Cards Cache] Loading explore cards for: ${cacheKey}`);
        }

        const cards = await fetchWithCache(url);
        if (!cards) {
            console.warn(`[Cards Cache] Failed to load explore cards for: ${cacheKey}`);
            // Cache empty array to prevent retry loops
            exploreCardsCache.set(cacheKey, []);
            return [];
        }
        const cardArray = Array.isArray(cards) ? cards : [];

        // Filter valid cards based on partner settings
        const validCards = cardArray.filter(card =>
            card && card.id && (!card.partner || !settings.PARTNERS || settings.PARTNERS[card.partner])
        );

        // Store in cache
        exploreCardsCache.set(cacheKey, validCards);
        return validCards;

    } catch (error) {
        console.error('[Cards Cache] Error loading explore cards:', error);
        // Cache empty array to prevent retry loops
        exploreCardsCache.set(cacheKey, []);
        return [];
    }
}


/**
 * Preload ALL cards for multiple packs in an area (batch optimization)
 * This is the high-level optimizer that replaces multiple-parallel-calls
 * @param {number} areaId - Area ID
 * @param {Array<number>} packIds - Array of Pack IDs
 * @returns {Promise<void>}
 */
export async function preloadAllCardsForArea(areaId, packIds) {
    if (!areaId || !Array.isArray(packIds) || packIds.length === 0) return;

    // Normalize IDs
    const aId = Number(areaId);
    const pIds = packIds.map(Number);

    const areaPreloadKey = String(aId);
    if (exploreAreaPreloadsInFlight.has(areaPreloadKey)) {
        if (LOGGING.ENABLED && LOGGING.CACHE) {
            console.log(`[Cards Cache] Area batch preload already in flight for Area: ${areaId}, waiting...`);
        }
        return exploreAreaPreloadsInFlight.get(areaPreloadKey);
    }

    // SYNC BARRIER: Create and store the promise immediately to block other calls
    const runPromise = (async () => {
        // Check if cards are enabled (await inside the promise)
        const settings = await getCardsSettings();
        if (!settings.ENABLED) return;
        try {
            if (LOGGING.ENABLED && LOGGING.CACHE) {
                console.log(`[Cards Cache] BATCH Preloading ALL cards for Area: ${aId}, Packs: ${pIds.join(',')}`);
            }

            const url = `/app_core/api/sqlite/get-cards-for-explore.php?areaId=${aId}&packIds=${pIds.join(',')}`;
            const data = await fetchWithCache(url);

            if (LOGGING.ENABLED && LOGGING.CACHE) {
                console.log(`[Cards Cache] Batch preload response received: ${Array.isArray(data) ? data.length : 0} cards.`);
            }

            if (!Array.isArray(data)) return;

            // PRE-INITIALIZE: Ensure all requested packs have a master map
            // This prevents "Not found" misses during the race while data is being processed
            pIds.forEach(pid => {
                const masterKey = `${aId}-${pid}`;
                if (!explorePackMasterCache.has(masterKey)) {
                    explorePackMasterCache.set(masterKey, new Map());
                }
            });

            // Group cards by pack_id and then tag_id to correctly populate exploreCardsCache
            const cardsByPackAndTag = {};
            data.forEach(card => {
                if (card && card.pack_id) {
                    // Filter valid cards (partner check)
                    if (!card.id || (card.partner && settings.PARTNERS && !settings.PARTNERS[card.partner])) {
                        return;
                    }

                    const pid = card.pack_id;
                    const tid = card.tag_id || 0; // Use 0 for cards without specific tag

                    if (!cardsByPackAndTag[pid]) cardsByPackAndTag[pid] = {};
                    if (!cardsByPackAndTag[pid][tid]) cardsByPackAndTag[pid][tid] = [];

                    cardsByPackAndTag[pid][tid].push(card);
                }
            });
            // Populate cards cache for each pack-tag combination found
            Object.keys(cardsByPackAndTag).forEach(pid => {
                const packCardsByTag = cardsByPackAndTag[pid];
                const packIdNum = Number(pid);
                const masterKey = `${aId}-${packIdNum}`;

                // Initialize master cache for this pack
                if (!explorePackMasterCache.has(masterKey)) {
                    explorePackMasterCache.set(masterKey, new Map());
                }
                const masterMap = explorePackMasterCache.get(masterKey);

                Object.keys(packCardsByTag).forEach(tid => {
                    const tagIdNum = Number(tid);
                    const tagIds = tagIdNum === 0 ? [] : [tagIdNum];
                    const cacheKey = getExploreCacheKey(aId, packIdNum, tagIds);
                    const cards = packCardsByTag[tid];

                    exploreCardsCache.set(cacheKey, cards);
                    // Also store in master map for smart filtering
                    masterMap.set(tagIdNum, cards);
                });

                // Also populate the "all cards for pack" cache entry used by default views (no tags)
                // We need to collect all unique cards for this pack
                const allPackCardsMap = new Map();
                Object.values(packCardsByTag).forEach(cards => {
                    cards.forEach(c => allPackCardsMap.set(c.id, c));
                });
                const allPackCards = Array.from(allPackCardsMap.values());
                const rootCacheKey = getExploreCacheKey(aId, packIdNum, []);
                exploreCardsCache.set(rootCacheKey, allPackCards);
            });

            // IMPORTANT: Mark all requested packs as "fully loaded"
            // This prevents getCardsForExplore from calling the API if a pack-tag was empty
            pIds.forEach(pid => {
                explorePacksFullyLoaded.add(`${aId}-${pid}`);
            });

        } catch (error) {
            if (LOGGING.ENABLED && LOGGING.CACHE) {
                console.warn('[Cards Cache] Error in batch card preload:', error);
            }
        }
    })();

    exploreAreaPreloadsInFlight.set(areaPreloadKey, runPromise);
    runPromise.finally(() => exploreAreaPreloadsInFlight.delete(areaPreloadKey));
    return runPromise;
}


/**
 * Clear explore cards cache
 * @param {Object} params - Optional params to clear specific explore cache
 * @param {number} params.areaId - Area ID (optional)
 * @param {number} params.packId - Pack ID (optional)
 * @param {Array<number>} params.tagIds - Tag IDs (optional)
 */
export function clearExploreCardsCache(params = null) {
    if (params && params.areaId && params.packId) {
        const cacheKey = getExploreCacheKey(params.areaId, params.packId, params.tagIds || []);
        exploreCardsCache.delete(cacheKey);
        if (LOGGING.ENABLED && LOGGING.CACHE) {
            console.log(`[Cards Cache] Cleared explore cache for: ${cacheKey}`);
        }
    } else {
        exploreCardsCache.clear();
        if (LOGGING.ENABLED && LOGGING.CACHE) {
            console.log('[Cards Cache] Cleared all explore cards cache');
        }
    }
}

/**
 * Check if explore cards are cached
 * @param {number} areaId - Area ID
 * @param {number} packId - Pack ID
 * @param {Array<number>} tagIds - Array of tag IDs (optional)
 * @returns {boolean} True if cards are cached
 */
export function hasCachedExploreCards(areaId, packId, tagIds = []) {
    const cacheKey = getExploreCacheKey(areaId, packId, tagIds);
    return exploreCardsCache.has(cacheKey);
}

// ===== MAP CARDS CACHING =====

/**
 * Get cache key for map cards (supports both mapId and mapSlug)
 * @param {number|string} mapId - Map ID or slug
 * @returns {string} Cache key
 */
function getMapCacheKey(mapId) {
    return String(mapId);
}

/**
 * Get cards for map (from cache or fetch if not cached)
 * @param {number|string} mapId - Map ID or slug
 * @returns {Promise<Array>} Array of cards for the map
 */
export async function getCardsForMap(mapId) {
    const cacheKey = getMapCacheKey(mapId);

    // Check cache first
    if (mapCardsCache.has(cacheKey)) {
        if (LOGGING.ENABLED && LOGGING.CACHE) {
            console.log(`[Cards Cache] Map cache HIT for: ${cacheKey}`);
        }
        return mapCardsCache.get(cacheKey);
    }

    // Check if cards are enabled
    const settings = await getCardsSettings();
    if (!settings.ENABLED) {
        return [];
    }

    // Not in cache, fetch from API
    try {
        const isNumeric = /^\d+$/.test(String(mapId));
        const param = isNumeric ? 'map_id' : 'map_slug';
        const url = `/app_core/api/sqlite/get-cards-for-map.php?${param}=${encodeURIComponent(mapId)}`;

        if (LOGGING.ENABLED && LOGGING.CACHE) {
            console.log(`[Cards Cache] Loading map cards for: ${cacheKey}`);
        }

        const cards = await fetchWithCache(url);
        if (!cards) {
            console.warn(`[Cards Cache] Failed to load map cards for: ${cacheKey}`);
            // Cache empty array to prevent retry loops
            mapCardsCache.set(cacheKey, []);
            return [];
        }
        const cardArray = Array.isArray(cards) ? cards : [cards];

        // Filter valid cards based on partner settings
        const validCards = cardArray.filter(card =>
            card && card.id && (!card.partner || !settings.PARTNERS || settings.PARTNERS[card.partner])
        );

        // Store in cache
        mapCardsCache.set(cacheKey, validCards);
        return validCards;

    } catch (error) {
        console.error('[Cards Cache] Error loading map cards:', error);
        // Cache empty array to prevent retry loops
        mapCardsCache.set(cacheKey, []);
        return [];
    }
}

/**
 * Preload map cards (load in background)
 * @param {number|string} mapId - Map ID or slug
 * @returns {Promise<void>} Resolves when cards are loaded (or failed)
 */
export async function preloadCardsForMap(mapId) {
    const cacheKey = getMapCacheKey(mapId);

    // Skip if already cached
    if (mapCardsCache.has(cacheKey)) {
        return;
    }

    // Don't await - load in background
    getCardsForMap(mapId).catch(error => {
        console.warn('[Cards Cache] Error preloading map cards:', error);
    });
}

/**
 * Clear map cards cache
 * @param {number|string} mapId - Map ID or slug (optional, clears all if not provided)
 */
export function clearMapCardsCache(mapId = null) {
    if (mapId) {
        const cacheKey = getMapCacheKey(mapId);
        mapCardsCache.delete(cacheKey);
        if (LOGGING.ENABLED && LOGGING.CACHE) {
            console.log(`[Cards Cache] Cleared map cache for: ${cacheKey}`);
        }
    } else {
        mapCardsCache.clear();
        if (LOGGING.ENABLED && LOGGING.CACHE) {
            console.log('[Cards Cache] Cleared all map cards cache');
        }
    }
}

/**
 * Check if map cards are cached
 * @param {number|string} mapId - Map ID or slug
 * @returns {boolean} True if cards are cached
 */
export function hasCachedMapCards(mapId) {
    const cacheKey = getMapCacheKey(mapId);
    return mapCardsCache.has(cacheKey);
}

// ===== AREA CARDS CACHING =====

/**
 * Get cards for an area (from cache or fetch if not cached)
 * @param {number} areaId - Area ID
 * @returns {Promise<Array>} Array of cards for the area
 */
export async function getCardsForArea(areaId) {
    if (!areaId) return [];
    const cacheKey = String(areaId);

    // Check cache first
    if (areaCardsCache.has(cacheKey)) {
        if (LOGGING.ENABLED && LOGGING.CACHE) {
            console.log(`[Cards Cache] Area cache HIT for: ${cacheKey}`);
        }
        return areaCardsCache.get(cacheKey);
    }

    // Check if cards are enabled
    const settings = await getCardsSettings();
    if (!settings.ENABLED) {
        return [];
    }

    // Not in cache, fetch from API
    try {
        const url = `/app_core/api/sqlite/get-cards-for-area.php?areaId=${areaId}`;

        if (LOGGING.ENABLED && LOGGING.CACHE) {
            console.log(`[Cards Cache] Loading area cards for: ${cacheKey}`);
        }

        const cards = await fetchWithCache(url);
        if (!cards) {
            console.warn(`[Cards Cache] Failed to load area cards for: ${cacheKey}`);
            areaCardsCache.set(cacheKey, []);
            return [];
        }
        const cardArray = Array.isArray(cards) ? cards : (cards ? [cards] : []);

        // Filter valid cards based on partner settings
        const validCards = cardArray.filter(card =>
            card && card.id && (!card.partner || !settings.PARTNERS || settings.PARTNERS[card.partner])
        );

        // Store in cache
        areaCardsCache.set(cacheKey, validCards);
        return validCards;

    } catch (error) {
        console.error('[Cards Cache] Error loading area cards:', error);
        areaCardsCache.set(cacheKey, []);
        return [];
    }
}

/**
 * Preload area cards (load in background)
 * @param {number} areaId - Area ID
 */
export async function preloadCardsForArea(areaId) {
    if (!areaId) return;
    getCardsForArea(areaId).catch(() => { });
}

/**
 * Clear area cards cache
 * @param {number} areaId - Area ID (optional)
 */
export function clearAreaCardsCache(areaId = null) {
    if (areaId) {
        areaCardsCache.delete(String(areaId));
    } else {
        areaCardsCache.clear();
    }
}

